import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  type OnChanges,
  type OnInit,
  Output,
  type SimpleChanges,
} from '@angular/core';
import {
  type ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import type { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { BehaviorSubject, combineLatest, type Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

export interface SearchItem {
  id: string | number;
  name: string;
  email?: string;
  [key: string]: unknown;
}

@Component({
  selector: 'lib-cdw-search-input',
  templateUrl: './cdw-search-input.component.html',
  styleUrls: ['./cdw-search-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CdwSearchInputComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class CdwSearchInputComponent
  implements OnInit, OnChanges, ControlValueAccessor
{
  @Input() placeholder = 'Search...';
  @Input() items: SearchItem[] = [];
  @Input() displayField = 'name';
  @Input() secondaryField = 'email';
  @Input() iconPrefix = 'search';
  @Input() backgroundColor = 'var(--color-primary-gray-50)';
  @Input() iconColor = 'var(--color-tertiary-blue-cta)';
  @Input() height = '40px';

  @Output() itemSelected = new EventEmitter<SearchItem>();

  searchControl = new FormControl('');
  filteredItems$!: Observable<SearchItem[]>;
  private items$ = new BehaviorSubject<SearchItem[]>([]);

  private onChange = (value: unknown) => {
    // No-op: This will be replaced by Angular's ControlValueAccessor
  };

  private onTouched = () => {
    // No-op: This will be replaced by Angular's ControlValueAccessor
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items']) {
      this.items$.next(this.items);
    }
  }

  ngOnInit(): void {
    // Initialize items$ with current items
    this.items$.next(this.items);

    // Combine search control value changes with items changes
    this.filteredItems$ = combineLatest([
      this.searchControl.valueChanges.pipe(startWith('')),
      this.items$,
    ]).pipe(
      map(([searchValue, items]) => {
        const filterValue = searchValue || '';
        const filtered = this._filterItems(filterValue, items);
        return filtered;
      })
    );
  }

  private _filterItems(
    value: string | SearchItem,
    items: SearchItem[]
  ): SearchItem[] {
    const filterValue =
      typeof value === 'string'
        ? value.toLowerCase().trim()
        : (value[this.displayField] as string).toLowerCase().trim();

    if (!filterValue) {
      return items;
    }

    const startsWithMatches: SearchItem[] = [];
    const containsMatches: SearchItem[] = [];

    for (const item of items) {
      const primaryValue = (item[this.displayField] as string).toLowerCase();
      const secondaryValue = this.secondaryField
        ? (item[this.secondaryField] as string)?.toLowerCase()
        : '';

      const primaryStartsWith = primaryValue.startsWith(filterValue);
      const primaryContains = primaryValue.includes(filterValue);
      const secondaryContains = secondaryValue?.includes(filterValue) ?? false;

      if (primaryStartsWith) {
        startsWithMatches.push(item);
      } else if (primaryContains || secondaryContains) {
        containsMatches.push(item);
      }
    }

    return [...startsWithMatches, ...containsMatches];
  }

  public onItemSelected(event: MatAutocompleteSelectedEvent): void {
    const selectedItem = event.option.value as SearchItem;
    this.itemSelected.emit(selectedItem);
    this.searchControl.setValue('');
    this.onChange('');
  }

  public displayItem(): string {
    return ''; // Return empty string to clear the input after selection
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    this.searchControl.setValue(value, { emitEvent: false });
  }

  registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.searchControl.disable();
    } else {
      this.searchControl.enable();
    }
  }
}
